import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FaEdit, FaTrash, FaPrint } from 'react-icons/fa';
import { api } from '../api';
import FichasHeader from '../components/FichasHeader';

const PageStyle = createGlobalStyle`@media print { body { display: none; } }`;

function calcTotal(orden) {
  const importe = (orden.items || []).reduce((s, it) => {
    return s + (parseFloat(it.cantidad) || 0) * (parseFloat(it.precioUnit) || 0);
  }, 0);
  const iva = importe * ((parseFloat(orden.ivaPorc) || 0) / 100);
  return importe + iva;
}

function fmt(n) {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function OrdenesLista() {
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getOrdenes()
      .then(data => setOrdenes(data || []))
      .catch(() => setOrdenes([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta orden de compra?')) return;
    await api.deleteOrden(id).catch(() => {});
    setOrdenes(prev => prev.filter(o => o.id !== id));
  };

  return (
    <>
      <PageStyle />
      <Page>
        <FichasHeader />
        <Content>
          {loading ? (
            <Empty><p>Cargando órdenes…</p></Empty>
          ) : ordenes.length === 0 ? (
            <Empty>
              <EmptyIcon>🛒</EmptyIcon>
              <p>No hay órdenes de compra guardadas.</p>
              <CreateBtn onClick={() => navigate('/fichas/ordenes/nueva')}>
                + Crear primera orden
              </CreateBtn>
            </Empty>
          ) : (
            <TableWrap>
              <OcTable>
                <thead>
                  <tr>
                    <OcTh>Folio</OcTh>
                    <OcTh>Fecha</OcTh>
                    <OcTh>Proveedor</OcTh>
                    <OcTh $right>Total</OcTh>
                    <OcTh $center>Acciones</OcTh>
                  </tr>
                </thead>
                <tbody>
                  {ordenes.map(o => (
                    <tr key={o.id}>
                      <OcTd $bold>{o.folio || '—'}</OcTd>
                      <OcTd>{o.fecha || '—'}</OcTd>
                      <OcTd>{o.provNombre || <em style={{ color: 'var(--gray)', fontStyle: 'italic' }}>Sin proveedor</em>}</OcTd>
                      <OcTd $right $mono>${fmt(calcTotal(o))}</OcTd>
                      <OcTd $center>
                        <ActionGroup>
                          <IconBtn title="Editar" onClick={() => navigate(`/fichas/ordenes/editar/${o.id}`)}>
                            <FaEdit />
                          </IconBtn>
                          <IconBtn title="Imprimir / PDF" onClick={() => navigate(`/fichas/ordenes/editar/${o.id}?print=1`)}>
                            <FaPrint />
                          </IconBtn>
                          <IconBtn $danger title="Eliminar" onClick={() => handleDelete(o.id)}>
                            <FaTrash />
                          </IconBtn>
                        </ActionGroup>
                      </OcTd>
                    </tr>
                  ))}
                </tbody>
              </OcTable>
            </TableWrap>
          )}
        </Content>
      </Page>
    </>
  );
}

/* ── Styled components ── */
const Page = styled.div`
  min-height: 100vh;
  background: var(--mid);
  padding-bottom: 3rem;
`;

const Content = styled.main`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 0 1.5rem;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: var(--gray);
  font-size: 1rem;
  text-align: center;
`;

const EmptyIcon = styled.div`font-size: 3rem;`;

const CreateBtn = styled.button`
  background: var(--red);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.65rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
`;

const TableWrap = styled.div`
  background: white;
  border-top: 3px solid var(--blue);
  overflow-x: auto;
`;

const OcTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
`;

const OcTh = styled.th`
  background: var(--blue);
  color: white;
  padding: 0.7rem 1rem;
  text-align: ${p => p.$right ? 'right' : p.$center ? 'center' : 'left'};
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const OcTd = styled.td`
  padding: 0.7rem 1rem;
  border-bottom: 1px solid var(--border);
  text-align: ${p => p.$right ? 'right' : p.$center ? 'center' : 'left'};
  font-weight: ${p => p.$bold ? '700' : '400'};
  font-family: ${p => p.$mono ? 'monospace' : 'Inter, sans-serif'};
  color: ${p => p.$bold ? 'var(--blue)' : 'var(--text)'};
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.25rem;
  justify-content: center;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 4px;
  background: ${p => p.$danger ? 'var(--red-lt)' : 'var(--blue-lt)'};
  color: ${p => p.$danger ? 'var(--red)' : 'var(--blue)'};
  cursor: pointer;
  font-size: 0.82rem;
  transition: opacity 0.12s;
  &:hover { opacity: 0.75; }
`;
